import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logger from '../utils/logger';

const IntegrationsPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [connectionStates, setConnectionStates] = useState({});
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const highlightIntegration = searchParams.get('highlight');
  
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://www.whydiditdie.com';

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleOAuthCallback = async () => {
      const connection = searchParams.get('connection');
      const status = searchParams.get('status');
      const userId = searchParams.get('user_id');
      const error = searchParams.get('error');
      
      logger.info('🔍 OAuth callback detected:', { connection, status, userId, error });
      
      if (connection && status) {
        if (status === 'success') {
          logger.info('✅ OAuth Success:', { connection, userId });

          if (connection === 'facebook') {
            const accessToken = searchParams.get('access_token'); // optional, if passed back from backend
            
            try {
              // This is what Meta expects to detect Facebook Ads usage
              fetch(`https://graph.facebook.com/v18.0/me/adaccounts?access_token=${accessToken}`, {
                method: 'GET'
              }).then(res => res.json()).then(data => {
                logger.info('📡 Facebook /me/adaccounts response:', data);
              });
            } catch (err) {
              logger.error('❌ Failed to call /me/adaccounts:', err);
            }
          }
          
          logger.info(`✅ ${connection} connected successfully`);
          
          const url = new URL(window.location);
          url.searchParams.delete('connection');
          url.searchParams.delete('status');
          url.searchParams.delete('user_id');
          url.searchParams.delete('error');
          
          window.history.replaceState({}, '', url.pathname);
          
          setTimeout(() => {
            logger.info('🏠 Navigating to dashboard...');
            navigate('/dashboard', { replace: true });
          }, 1000);
          
        } else if (status === 'error') {
          logger.error('❌ OAuth Error:', error);
          const errorMessage = error || 'Unknown error occurred';
          alert(`Failed to connect ${connection}: ${errorMessage}. Please try again.`);
          
          const url = new URL(window.location);
          url.searchParams.delete('connection');
          url.searchParams.delete('status');
          url.searchParams.delete('user_id');
          url.searchParams.delete('error');
          window.history.replaceState({}, '', url.pathname);
        }
      }
    };
    
    if (searchParams.has('connection') && searchParams.has('status')) {
      handleOAuthCallback();
    }
    
  }, [searchParams, navigate]);

  const setConnectionState = (integrationSlug, state) => {
    setConnectionStates(prev => ({
      ...prev,
      [integrationSlug]: state
    }));
  };

  const handleIntegrationConnect = async (integration) => {
    if (integration.status !== 'active') {
      alert(`${integration.name} integration is coming soon!`);
      return;
    }
    
    logger.info('🔄 Connecting to integration:', integration.name);
    setConnectionState(integration.slug, 'connecting');
    
    if (!isAuthenticated || !user) {
      logger.info('❌ User not authenticated, redirecting to login');
      alert('Please log in first to connect your accounts');
      navigate('/login');
      return;
    }
    
    try {
      logger.info('🚀 Starting OAuth flow for:', integration.name);
      logger.info('👤 User ID from AuthContext:', user.id);
      
      if (!BACKEND_URL) {
        throw new Error('Backend URL not configured. Please check REACT_APP_BACKEND_URL environment variable.');
      }
      
      const oauthUrl = `${BACKEND_URL}/api/auth/${integration.slug}?userId=${encodeURIComponent(user.id)}`;
      
      logger.info('🔗 Redirecting to OAuth URL with userId:', oauthUrl);
      
      setConnectionState(integration.slug, 'redirecting');
      
      setTimeout(() => {
        logger.info('⏳ Executing redirect to OAuth provider...');
        window.location.href = oauthUrl;
      }, 500);
      
    } catch (error) {
      logger.error('🚨 Integration connect error:', error);
      setConnectionState(integration.slug, 'error');
      
      alert(`Connection Error: ${error.message}

Backend URL: ${BACKEND_URL}

Make sure:
1. Your backend server is running at ${BACKEND_URL}
2. REACT_APP_BACKEND_URL is correctly set in your .env file
3. Backend is accessible from your browser
4. Facebook app redirect URI is configured correctly`);
    }
  };

  const testBackendConnectivity = async () => {
    try {
      if (!BACKEND_URL) {
        alert('❌ Backend URL not configured in environment variables');
        return;
      }

      logger.info('🧪 Testing backend connectivity...');
      
      const testUrl = `${BACKEND_URL}/api/health`;
      
      const response = await fetch(testUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        alert(`✅ Backend is reachable!

Status: ${result.status}
Facebook Config: ${result.env.facebook_configured ? 'OK' : 'Missing'}
Supabase Config: ${result.env.supabase_configured ? 'OK' : 'Missing'}
Frontend URL: ${result.env.frontend_url}
Backend URL: ${result.env.backend_url}`);
      } else {
        alert(`❌ Backend responded with error: ${response.status} ${response.statusText}`);
      }
      
    } catch (error) {
      logger.error('❌ Backend connectivity test failed:', error);
      alert(`❌ Cannot reach backend: ${error.message}

Backend URL: ${BACKEND_URL}

This could mean:
1. Backend server is not running
2. CORS issues
3. Network connectivity problems
4. Wrong URL configuration`);
    }
  };

  const checkExistingConnections = async () => {
    try {
      if (!user?.id) return;

      logger.info('🔍 Checking existing connections for user:', user.id);
      logger.info('TODO: Check existing Facebook connections for user');
      
    } catch (error) {
      logger.error('Error checking connections:', error);
    }
  };

  useEffect(() => {
    if (user?.id) {
      checkExistingConnections();
    }
  }, [user]);

  const getConnectionButtonText = (integration) => {
    const state = connectionStates[integration.slug];
    switch (state) {
      case 'connecting':
        return 'Connecting...';
      case 'redirecting':
        return 'Redirecting to Facebook...';
      case 'error':
        return 'Retry Connection';
      default:
        return 'Click to connect →';
    }
  };

  const getConnectionButtonClass = (integration) => {
    const state = connectionStates[integration.slug];
    const baseClass = "text-sm font-semibold transition-colors";
    
    switch (state) {
      case 'connecting':
      case 'redirecting':
        return `${baseClass} text-green-500 opacity-70 cursor-not-allowed`;
      case 'error':
        return `${baseClass} text-red-500`;
      default:
        return `${baseClass} text-green-500`;
    }
  };

  const integrationCategories = [
    {
      title: "Advertising Platforms",
      icon: "📱",
      description: "Connect your ad accounts for comprehensive campaign forensics",
      integrations: [
        { 
          name: "Facebook Ads", 
          slug: "facebook", 
          provider: "facebook", 
          icon: "📘", 
          description: "Full campaign analysis and waste detection", 
          status: "active" 
        },
        { 
          name: "Facebook Pages", 
          slug: "facebook-pages", 
          provider: "facebook", 
          icon: "📄", 
          description: "Verify page management for Marketing API access", 
          status: "active" 
        },
        { name: "Google Ads", slug: "google", provider: "google", icon: "🔍", description: "Search and display campaign forensics", status: "coming_soon" },
        { name: "LinkedIn Ads", slug: "linkedin", provider: "linkedin", icon: "💼", description: "B2B campaign intelligence", status: "coming_soon" },
        { name: "TikTok Ads", slug: "tiktok", provider: "tiktok", icon: "🎵", description: "Social video campaign analysis", status: "coming_soon" },
        { name: "Twitter Ads", slug: "twitter", provider: "twitter", icon: "🐦", description: "Social media campaign tracking", status: "coming_soon" },
        { name: "Snapchat Ads", slug: "snapchat", provider: "snapchat", icon: "👻", description: "Mobile-first campaign analysis", status: "coming_soon" },
        { name: "Pinterest Ads", slug: "pinterest", provider: "pinterest", icon: "📌", description: "Visual content campaign tracking", status: "coming_soon" },
        { name: "YouTube Ads", slug: "youtube", provider: "youtube", icon: "📺", description: "Video advertising analysis", status: "coming_soon" }
      ]
    }
  ];

  const getStatusColorClass = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'coming_soon': return 'bg-amber-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Active';
      case 'coming_soon': return 'Coming Soon';
      default: return 'Planned';
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-900 to-slate-900 text-white">
        <div className="text-center">
          <div className="text-4xl mb-4">🔗</div>
          <div className="text-xl">Loading Integrations...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-900 text-white min-h-screen">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 lg:px-8 py-4 transition-all duration-300 ${
        scrollY > 50 
          ? 'bg-zinc-900/95 backdrop-blur-xl border-b border-white/10' 
          : 'bg-transparent'
      }`}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div 
            className="flex items-center gap-2 cursor-pointer group"
            onClick={() => navigate('/dashboard')}
          >
            <span className="text-2xl">🕵🏽</span>
            <span className="text-xl font-bold text-red-500 group-hover:text-red-400 transition-colors">
              WhyDidItDie
            </span>
          </div>
          
          {user && (
            <div className="text-green-500 text-sm">
              {user.email}
            </div>
          )}
        </div>
      </nav>

      <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black mb-4 bg-gradient-to-r from-white via-orange-400 to-red-500 bg-clip-text text-transparent">
              8+ Integrations & Growing
            </h1>
            <p className="text-lg sm:text-xl text-white/70 max-w-2xl mx-auto mb-8">
              Connect WhyDidItDie to your advertising platforms and get forensic insights across all your campaigns
            </p>
            
            {/* Auth Status */}
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg px-4 py-3 max-w-md mx-auto text-sm text-green-500">
              {isAuthenticated 
                ? `✅ Logged in as ${user?.email}` 
                : '⚠️ Please log in to connect accounts'
              }
            </div>
          </div>

          {/* Integration Categories */}
          {integrationCategories.map((category, categoryIndex) => (
            <div key={categoryIndex} className="mb-16">
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 mb-8 text-center sm:text-left">
                <span className="text-5xl">{category.icon}</span>
                <div>
                  <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-orange-400 mb-2">
                    {category.title}
                  </h2>
                  {category.description && (
                    <p className="text-white/70 text-base sm:text-lg">
                      {category.description}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {category.integrations.map((integration, index) => (
                  <div
                    key={index}
                    onClick={() => handleIntegrationConnect(integration)}
                    className={`
                      relative p-6 bg-white/5 rounded-xl border border-white/10 
                      transition-all duration-300 
                      ${highlightIntegration === integration.slug ? 'ring-2 ring-orange-400 bg-orange-400/10' : ''}
                      ${integration.status === 'active' && isAuthenticated 
                        ? 'cursor-pointer hover:bg-white/10 hover:-translate-y-1 hover:shadow-2xl hover:shadow-black/50' 
                        : 'cursor-default opacity-60'
                      }
                    `}
                  >
                    <div className={`
                      absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-bold text-white
                      ${getStatusColorClass(integration.status)}
                    `}>
                      {getStatusText(integration.status)}
                    </div>

                    <div className="flex items-center gap-3 mb-4 mt-8">
                      <span className="text-4xl">{integration.icon}</span>
                      <h3 className="text-xl font-bold text-white">
                        {integration.name}
                      </h3>
                    </div>

                    <p className="text-white/80 text-sm leading-relaxed mb-4">
                      {integration.description}
                    </p>

                    {integration.status === 'active' && (
                      <div className={isAuthenticated ? getConnectionButtonClass(integration) : "text-sm font-semibold text-amber-500"}>
                        {isAuthenticated ? getConnectionButtonText(integration) : 'Login to connect'}
                      </div>
                    )}

                    {integration.status === 'coming_soon' && (
                      <div className="text-sm font-semibold text-amber-500">
                        Coming Soon
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Debug Tools - Development Only
          {process.env.NODE_ENV === 'development' && (
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-8">
              <h3 className="text-blue-400 text-lg font-bold mb-4">🔧 Debug Tools</h3>
              <button
                onClick={testBackendConnectivity}
                className="bg-blue-500/20 border border-blue-500/50 text-blue-400 px-4 py-2 rounded-md text-sm hover:bg-blue-500/30 transition-colors"
              >
                Test Backend Connectivity
              </button>
            </div>
          )} */}

          {/* CTA Section */}
          <div className="text-center p-8 sm:p-12 bg-orange-400/10 rounded-2xl border border-orange-400/20 mt-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 text-white">
              Ready to Connect Your Platforms?
            </h2>
            <p className="text-lg sm:text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              Join our beta and start getting forensic insights across all your advertising platforms today
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              {!isAuthenticated ? (
                <>
                  <button
                    onClick={() => navigate('/signup')}
                    className="w-full sm:w-auto bg-orange-400 text-white px-8 py-4 rounded-xl text-lg font-bold hover:-translate-y-1 hover:shadow-2xl hover:shadow-orange-500/40 transition-all duration-300"
                  >
                    Join Beta Free
                  </button>
                  <button
                    onClick={() => navigate('/login')}
                    className="w-full sm:w-auto bg-transparent border-2 border-white/30 text-white px-8 py-4 rounded-xl text-lg font-bold hover:bg-white/10 hover:border-white/50 hover:-translate-y-1 transition-all duration-300"
                  >
                    Login
                  </button>
                </>
              ) : (
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full sm:w-auto bg-green-500 text-white px-8 py-4 rounded-xl text-lg font-bold hover:-translate-y-1 hover:shadow-2xl hover:shadow-green-500/40 transition-all duration-300"
                >
                  Go to Dashboard
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .loading::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 16px;
          height: 16px;
          margin: -8px 0 0 -8px;
          border: 2px solid #10b981;
          border-radius: 50%;
          border-top-color: transparent;
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default IntegrationsPage;